import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api } from '../lib/api'
import { useAuth } from '../auth/AuthContext'
import { Roles } from '../auth/rbac'
import MenuGuideButton from '../components/MenuGuideButton'
import { formatMenuInstant } from '../lib/date'

export default function SupportInquiriesPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isOperator = useMemo(() => (user?.roles || []).includes(Roles.OPERATOR), [user?.roles])

  // 운영자는 문의를 "작성"하지 않고, 들어온 문의를 처리하는 용도이므로 기본은 전체 조회
  const [scope, setScope] = useState(isOperator ? 'all' : 'mine') // mine|all
  const [tab, setTab] = useState('CUSTOMER_SUPPORT') // CUSTOMER_SUPPORT | ACCOUNT_RELATED | UW3NN_EXCEPTION
  const [unreadCounts, setUnreadCounts] = useState({ CUSTOMER_SUPPORT: 0, ACCOUNT_RELATED: 0, UW3NN_EXCEPTION: 0 })
  const [sourceFilter, setSourceFilter] = useState('ALL') // ALL | LANDING | APP
  const [loading, setLoading] = useState(false)
  const [items, setItems] = useState([])
  const [err, setErr] = useState('')

  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [creating, setCreating] = useState(false)

  useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setErr('')
      try {
        const res = await api.get('/inquiries', { params: { scope, category: tab } })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        const list = Array.isArray(payload?.content) ? payload.content : (Array.isArray(payload) ? payload : [])
        if (!ignore) setItems(list)
      } catch (e) {
        console.error(e)
        if (!ignore) setErr('문의 목록을 불러오지 못했습니다.')
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [scope, tab])

  // 운영자 탭 배지(미확인 문의 개수)
  useEffect(() => {
    if (!isOperator) return
    let ignore = false
    let timer = null
    async function loadCounts() {
      try {
        const res = await api.get('/inquiries/unread-counts')
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (ignore) return
        setUnreadCounts({
          CUSTOMER_SUPPORT: Number(payload?.CUSTOMER_SUPPORT || 0),
          ACCOUNT_RELATED: Number(payload?.ACCOUNT_RELATED || 0),
          UW3NN_EXCEPTION: Number(payload?.UW3NN_EXCEPTION || 0),
        })
      } catch {
        // ignore
      }
    }
    loadCounts()
    timer = setInterval(loadCounts, 15000)
    return () => {
      ignore = true
      try { if (timer) clearInterval(timer) } catch { }
    }
  }, [isOperator])

  const onSubmit = async (e) => {
    e.preventDefault()
    if (!title.trim() || !content.trim()) return
    try {
      setCreating(true)
      const category = tab === 'ACCOUNT_RELATED' ? 'ACCOUNT_RELATED' : 'CUSTOMER_SUPPORT'
      const res = await api.post('/inquiries', { title: title.trim(), content: content.trim(), category })
      const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
      const id = payload?.id
      setTitle('')
      setContent('')
      if (id) navigate(`/support/inquiries/${id}`)
      else navigate('/support/inquiries')
    } catch (e2) {
      console.error(e2)
      setErr('문의 등록에 실패했습니다.')
    } finally {
      setCreating(false)
    }
  }

  const filteredItems = useMemo(() => {
    if (!isOperator || sourceFilter === 'ALL') return items
    if (sourceFilter === 'LANDING') return items.filter((it) => it?.source === 'LANDING')
    return items.filter((it) => (it?.source || 'APP') !== 'LANDING')
  }, [isOperator, items, sourceFilter])

  const landingCount = useMemo(() => items.filter((it) => it?.source === 'LANDING').length, [items])
  const answeredCount = useMemo(() => filteredItems.filter((it) => it?.status === 'ANSWERED').length, [filteredItems])

  return (
    <section className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-bold">고객문의</h1>
            <MenuGuideButton slug="support-inquiries" title="고객문의 가이드" />
          </div>
        </div>
        <div className="text-xs text-slate-500">
          {user?.name ? <div>로그인: {user.name}</div> : null}
        </div>
      </div>

      {/* Tabs (고객지원 | 계정관련 | 예외질환) */}
      <div className="flex items-center gap-2 border-b">
        {[
          { id: 'CUSTOMER_SUPPORT', label: '고객지원' },
          { id: 'ACCOUNT_RELATED', label: '계정관련' },
          { id: 'UW3NN_EXCEPTION', label: '예외질환' },
        ].map((t) => {
          const active = tab === t.id
          const hasNew = isOperator && Number(unreadCounts?.[t.id] || 0) > 0
          return (
            <button
              key={t.id}
              type="button"
              className={`px-4 py-2 text-sm -mb-px border-b-2 ${active ? 'border-brand-sky text-brand-sky font-semibold' : 'border-transparent text-slate-600 hover:text-slate-900'}`}
              onClick={() => setTab(t.id)}
            >
              <span className="relative inline-flex items-center">
                {t.label}
                {hasNew && (
                  <span className="ml-2 inline-flex items-center justify-center">
                    <span className="inline-block h-2 w-2 rounded-full bg-red-500" aria-label="신규 문의" />
                  </span>
                )}
              </span>
            </button>
          )
        })}
      </div>

      {!isOperator && (tab === 'CUSTOMER_SUPPORT' || tab === 'ACCOUNT_RELATED') && (
        <div className="rounded border p-4 space-y-3">
          <div className="flex items-center justify-between gap-2">
            <div className="font-semibold text-sm">문의 작성</div>
            <div className="text-xs text-slate-500">운영자가 답글을 남기면 상태가 '답변완료'로 표시됩니다.</div>
          </div>
          {tab === 'ACCOUNT_RELATED' ? (
            <div className="rounded border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
              로그인, 회원유형, 조직 전환, 계정 삭제 등 계정 관련 문의는 이 탭에서 접수해 주세요.
              <div className="mt-2 flex items-center gap-2">
                <Link
                  to="/support/account-deletion"
                  className="rounded bg-red-600 text-white px-4 py-2 text-sm font-medium hover:bg-red-700 transition-colors"
                >
                  [계정 삭제 요청]
                </Link>
                <span className="text-xs text-amber-800">계정 및 데이터 삭제 요청은 별도 폼으로 접수할 수 있습니다.</span>
              </div>
            </div>
          ) : (
            <div className="text-xs text-slate-500">서비스 이용, 오류, 기능 문의 등 일반 문의를 접수할 수 있습니다.</div>
          )}
          <form onSubmit={onSubmit} className="space-y-3">
            <div className="space-y-1">
              <label className="text-sm text-slate-700">제목</label>
              <input className="w-full rounded border px-3 py-2" value={title} onChange={(e) => setTitle(e.target.value)} required />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-slate-700">내용</label>
              <textarea className="w-full rounded border px-3 py-2 min-h-[140px]" value={content} onChange={(e) => setContent(e.target.value)} required />
            </div>
            <div className="flex items-center gap-2">
              <button type="submit" className="rounded bg-brand-navy text-white px-4 py-2 text-sm disabled:opacity-60" disabled={creating}>
                {creating ? '등록 중…' : '문의 등록'}
              </button>
              <Link to="/" className="rounded border px-4 py-2 text-sm hover:bg-slate-50">대시보드</Link>
            </div>
          </form>
          {err && <div className="text-sm text-red-600">{err}</div>}
        </div>
      )}

      <div className="rounded border p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="font-semibold text-sm">문의 목록 {isOperator ? (tab === 'UW3NN_EXCEPTION' ? '(예외질환)' : (tab === 'ACCOUNT_RELATED' ? '(계정관련)' : '(고객지원)')) : ''}</div>
          <div className="flex items-center gap-2">
            {isOperator && (
              <select className="rounded border px-2 py-1 text-sm" value={scope} onChange={(e) => setScope(e.target.value)}>
                <option value="mine">내 문의</option>
                <option value="all">전체(운영자)</option>
              </select>
            )}
          </div>
        </div>

        {isOperator && tab !== 'UW3NN_EXCEPTION' && (
          <div className="flex flex-wrap items-center gap-2 rounded border bg-slate-50 p-3">
            {[
              { id: 'ALL', label: `전체 ${items.length}` },
              { id: 'LANDING', label: `랜딩 문의 ${landingCount}` },
              { id: 'APP', label: `일반 문의 ${Math.max(items.length - landingCount, 0)}` },
            ].map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                  sourceFilter === filter.id
                    ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10'
                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-100'
                }`}
                onClick={() => setSourceFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
            <div className="ml-auto text-xs text-slate-500">
              현재 {filteredItems.length}건 · 답변완료 {answeredCount}건
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-600">불러오는 중…</div>
        ) : (
          <div className="divide-y">
            {filteredItems.map((it) => (
              <Link key={it.id} to={`/support/inquiries/${it.id}`} className="block py-3 hover:bg-slate-50 px-2 rounded">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <div className="font-medium text-slate-800 truncate">{it.title}</div>
                      {it.source === 'LANDING' && (
                        <span className="rounded px-2 py-0.5 border bg-sky-50 border-sky-200 text-sky-700 text-[11px]">
                          랜딩 문의
                        </span>
                      )}
                    </div>
                    {isOperator && it.source === 'LANDING' && (
                      <div className="mt-1 text-xs text-slate-500">
                        {[
                          it.publicCompanyName,
                          it.publicName,
                          it.publicPhone,
                          it.publicEmail,
                        ].filter(Boolean).join(' · ') || '공개 문의'}
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-slate-500">{formatMenuInstant(it, 'createdAt')}</div>
                </div>
                <div className="flex items-center gap-2 mt-1 text-xs">
                  <span className={`rounded px-2 py-0.5 border ${it.status === 'ANSWERED' ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
                    {it.status === 'ANSWERED' ? '답변완료' : '접수'}
                  </span>
                  {isOperator && tab === 'UW3NN_EXCEPTION' && !it.operatorCheckedAt && (
                    <span className="rounded px-2 py-0.5 border bg-red-50 border-red-200 text-red-700">미확인</span>
                  )}
                  {typeof it.hasReply === 'boolean' && (
                    <span className="text-slate-500">{it.hasReply ? '답글 있음' : '답글 없음'}</span>
                  )}
                  {isOperator && it.creatorName && (
                    <span className="text-slate-500">작성자: {it.creatorName}</span>
                  )}
                  {isOperator && !it.creatorName && it.publicName && (
                    <span className="text-slate-500">문의자: {it.publicName}</span>
                  )}
                </div>
              </Link>
            ))}
            {filteredItems.length === 0 && (
              <div className="py-8 text-center text-sm text-slate-500">문의가 없습니다.</div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
